/**
 * Booking form modal (Private Events page).
 *
 * Mirrors modules/events.js's own open/close/aria-hidden/body-scroll-lock
 * contract (template-parts/event-modal-shell.php) rather than inventing a new one
 * -- the difference is that this modal's content is static markup already in
 * the DOM (section-templates/booking_form_modal.php renders one shared CF7
 * form), so there is no AJAX fetch step: every category card's
 * .js-booking-cta button opens the SAME instance.
 *
 * Close behavior (outside-click/Escape/close-button/focus-trap/focus-return)
 * is owned by modules/modal-behavior.js, shared with events.js, per
 * JCTCTAS-70 -- this file only owns opening the modal and its own
 * CF7-submit-triggered auto-close.
 */
import { initModalDismissal } from './modal-behavior.js';

export default function initBookingFormModal() {
    const modal = document.querySelector('.js-booking-modal');

    if (!modal) {
        return;
    }

    const modalController = initModalDismissal({
        modal,
        panelSelector: '.c-booking-modal__panel',
        closeSelector: '.js-booking-modal-close',
        // Double rAF, not a synchronous .focus() call right after open() adds
        // `is-open` (confirmed real, JCTCTAS-236): `.c-booking-modal` opens via
        // a CSS `visibility: hidden -> visible` transition
        // (assets/src/scss/components/_booking-modal.scss), and reading
        // getComputedStyle(modal).visibility in the SAME synchronous tick as
        // the classList.add() still reports "hidden" -- calling .focus() on
        // an element that's still `visibility: hidden` at that instant is a
        // no-op (activeElement silently stays on <body>). The event modal
        // never hit this because its own initial-focus call
        // (modules/events.js) only ever runs after its AJAX content fetch
        // resolves, long after this transition has settled. A single rAF is
        // NOT reliably enough here either (confirmed empirically: still
        // reports "hidden" one frame later in this exact browser); two
        // nested rAF callbacks reliably do. Silent focus failure meant Tab
        // from a real click never actually entered the modal at all --
        // keyboard users tabbed through the page BEHIND it instead, which is
        // what made this look like a broken focus-trap.
        onOpen: () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    const firstField = modal.querySelector('input, textarea, select, button');
                    firstField?.focus();
                });
            });
        },
    });

    document.addEventListener('click', (event) => {
        const trigger = event.target.closest('.js-booking-cta');
        if (trigger) {
            event.preventDefault();
            modalController.open(trigger);
        }
    });

    // CF7 6.1.5 dispatches this native, bubbling CustomEvent on the <form>
    // element itself after a successful send -- no prior listener existed
    // for it anywhere in the theme, so the modal previously just sat open
    // with CF7's own inline "Thank you" message inside it and no other
    // feedback that anything happened. A short delay lets the visitor
    // actually read that message before the modal disappears, rather than
    // closing instantly out from under it.
    modal.addEventListener('wpcf7mailsent', () => {
        if (!modalController.isOpen()) {
            return;
        }
        window.setTimeout(modalController.close, 3000);
    });
}
